import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { movies } from '@/mocks/movies';
import { characters } from '@/mocks/characters';
import { usePageMeta } from '@/hooks/usePageMeta';
import DetailHero from './components/DetailHero';
import CharacterList from './components/CharacterList';
import RelatedMovies from './components/RelatedMovies';

const tocItems = [
  { id: 'synopsis', label: '剧情简介', icon: 'ri-file-text-line' },
  { id: 'characters', label: '主要角色', icon: 'ri-group-line' },
  { id: 'related', label: '相关推荐', icon: 'ri-movie-line' },
];

export default function Detail() {
  const { id } = useParams();
  const movie = movies.find((m) => m.id === id);
  const [copied, setCopied] = useState(false);

  usePageMeta(
    movie ? `${movie.title} - 剧情介绍与角色 - 玉女心经2` : '影视详情 - 玉女心经2',
    movie ? `${movie.title}（${movie.year}）剧情简介、角色信息与作品背景资料，${movie.synopsis.slice(0, 40)}…` : ''
  );

  if (!movie) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
          <i className="ri-film-line text-3xl"></i>
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">未找到该影视作品</h1>
        <p className="mt-2 text-sm text-foreground-600">它可能已被移除，或者链接有误。</p>
        <Link
          to="/category"
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i> 返回影视分类
        </Link>
      </div>
    );
  }

  const movieCharacters = characters.filter((c) => c.movieId === movie.id);
  let related = movies.filter((m) => m.id !== movie.id && m.category === movie.category);
  if (related.length < 3) {
    const fill = movies.filter((m) => m.id !== movie.id && m.category !== movie.category);
    related = [...related, ...fill].slice(0, 3);
  } else {
    related = related.slice(0, 3);
  }

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <div className="w-full bg-background-50 px-4 md:px-8 pt-6">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视分类', to: '/category' }, { label: movie.title }]} />
      </div>

      <DetailHero movie={movie} onShare={handleShare} copied={copied} />

      <div className="w-full bg-background-50 px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-10">
            <section id="synopsis" className="scroll-mt-24">
              <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-file-text-line"></i>
                </span>
                剧情简介
              </h2>
              <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">{movie.synopsis}</p>
              <div className="mt-6 grid grid-cols-1 gap-3 rounded-lg border border-background-200/70 bg-background-100 p-5 sm:grid-cols-2">
                {[
                  { k: '导演', v: movie.director, i: 'ri-user-star-line' },
                  { k: '主演', v: movie.cast.join(' / '), i: 'ri-group-line' },
                  { k: '类型', v: movie.genres.join(' · '), i: 'ri-price-tag-3-line' },
                  { k: '上映年份', v: `${movie.year} · ${movie.region}`, i: 'ri-calendar-line' },
                  { k: '片长', v: movie.duration, i: 'ri-time-line' },
                  { k: '最近更新', v: movie.updatedAt, i: 'ri-refresh-line' },
                ].map((row) => (
                  <div key={row.k} className="flex items-start gap-2 text-sm">
                    <i className={`${row.i} mt-0.5 text-primary-600`}></i>
                    <span className="shrink-0 text-foreground-500">{row.k}</span>
                    <span className="text-foreground-800">{row.v}</span>
                  </div>
                ))}
              </div>
            </section>

            <section id="characters" className="scroll-mt-24">
              <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-group-line"></i>
                </span>
                主要角色
                <span className="font-label text-sm font-normal text-foreground-500">（{movieCharacters.length}）</span>
              </h2>
              <p className="mt-2 text-sm text-foreground-600">点击角色卡片，查看角色的背景介绍、性格分析与关联剧情点。</p>
              <div className="mt-5">
                <CharacterList characters={movieCharacters} />
              </div>
            </section>

            <section id="related" className="scroll-mt-24">
              <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-movie-line"></i>
                </span>
                相关影视推荐
              </h2>
              <div className="mt-5">
                <RelatedMovies movies={related} />
              </div>
            </section>
          </div>

          {/* 侧边目录 */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-4">
              <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-list-check-2 text-primary-600"></i> 本页目录
                </h3>
                <nav className="mt-4 space-y-1">
                  {tocItems.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className="flex items-center gap-2 rounded-md px-3 py-2 font-label text-sm text-foreground-700 transition-colors hover:bg-background-200/60 hover:text-primary-700"
                    >
                      <i className={item.icon}></i>
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>

              <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-share-line text-accent-600"></i> 分享本页
                </h3>
                <p className="mt-2 text-xs leading-6 text-foreground-600">
                  复制本页链接，分享给同样喜欢这部作品的朋友。
                </p>
                <button
                  type="button"
                  onClick={handleShare}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <i className={copied ? 'ri-check-line' : 'ri-link'}></i>
                  {copied ? '链接已复制' : '复制链接'}
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}